import { pushRegistration$ } from 'app/core/notifications/fcm-service';
import {
  deleteAuthToken,
  setAuthToken,
} from 'app/core/token/token-utils';
import { BLANK$ } from 'app/tools/utils';
import { Observable, timer } from 'rxjs';
import {
  finalize,
  map,
  mapTo,
  raceWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Merge } from 'type-fest';
import { ApiUrl } from '../api-core/api-constants';
import { ApiInfoRtn } from '../api-core/api-core.types';
import { apiPost } from '../api-core/api-request';
import apiRequestHookFactory from '../api-core/utility-factories/api-request-hook-factory';
import { apiRedux } from '../api-redux';
import { UserRole } from '../user/api-user.types';
import { currentUserRole } from '../user/user-utils';
import {
  AuthUserInputData,
  ChangePasswordReq,
  ConfirmResetPasswordReq,
  ExternalSignInReq,
  LoginReq,
  LoginRtn,
  RegistrationReq,
  RegistrationRtn,
  ResetPasswordReq,
} from './api-auth.types';

const NO_AUTH = { noAuth: true };

const addDeviceTokenAndRoleToData = <T>(
  data: T
): Observable<Merge<T, { deviceToken?: string; role: UserRole }>> =>
  BLANK$.pipe(
    withLatestFrom(pushRegistration$),
    map(([, deviceToken]) => ({
      ...data,
      deviceToken: deviceToken?.value,
      role: currentUserRole,
    }))
  );

export const useLogin = apiRequestHookFactory(
  apiRedux.login,
  (data: AuthUserInputData<LoginReq>): Observable<LoginRtn> =>
    addDeviceTokenAndRoleToData(data).pipe(
      switchMap(fullData =>
        apiPost<LoginRtn>(ApiUrl.LOGIN, fullData, NO_AUTH)
      ),
      tap(({ key }) => setAuthToken(key))
    )
);

export const useLogout = apiRequestHookFactory(
  apiRedux.logout,
  (): Observable<ApiInfoRtn> =>
    // NOTE: this races logout call against 1500ms. After that the auth token
    // is deleted anyway, and a first() operator prevents it happening twice.
    apiPost<ApiInfoRtn>(ApiUrl.LOGOUT, undefined, NO_AUTH).pipe(
      raceWith(timer(1500).pipe(mapTo({}))),
      finalize(deleteAuthToken)
    )
);

export const useRegisterUser = apiRequestHookFactory(
  apiRedux.registration,
  (data: AuthUserInputData<RegistrationReq>) =>
    addDeviceTokenAndRoleToData(data).pipe(
      switchMap(fullData =>
        apiPost<RegistrationRtn>(ApiUrl.REGISTER, fullData, NO_AUTH)
      ),
      tap(({ key }) => setAuthToken(key))
    )
);

export const useAppleExternalSignIn = apiRequestHookFactory(
  apiRedux.externalSignIn,
  (data: AuthUserInputData<ExternalSignInReq>) =>
    addDeviceTokenAndRoleToData(data).pipe(
      switchMap(fullData =>
        apiPost<LoginRtn>(ApiUrl.EXTERNAL_SIGN_IN, fullData, NO_AUTH)
      ),
      tap(({ key }) => setAuthToken(key))
    )
);

export const useFacebookExternalSignIn = apiRequestHookFactory(
  apiRedux.externalSignIn,
  (data: AuthUserInputData<ExternalSignInReq>) =>
    addDeviceTokenAndRoleToData(data).pipe(
      switchMap(fullData =>
        apiPost<LoginRtn>(ApiUrl.EXTERNAL_SIGN_IN, fullData, NO_AUTH)
      ),
      tap(({ key }) => setAuthToken(key))
    )
);

export const useGoogleExternalSignIn = apiRequestHookFactory(
  apiRedux.externalSignIn,
  (data: AuthUserInputData<ExternalSignInReq>) =>
    addDeviceTokenAndRoleToData(data).pipe(
      switchMap(fullData =>
        apiPost<LoginRtn>(ApiUrl.EXTERNAL_SIGN_IN, fullData, NO_AUTH)
      ),
      tap(({ key }) => setAuthToken(key))
    )
);

export const useChangePassword = apiRequestHookFactory(
  apiRedux.changePassword,
  (data: ChangePasswordReq) =>
    apiPost(ApiUrl.CHANGE_PASSWORD, data).pipe(
      mapTo({ detail: 'Password changed successfully' })
    )
);

export const useConfirmResetPassword = apiRequestHookFactory(
  apiRedux.confirmResetPassword,
  (data: ConfirmResetPasswordReq) =>
    apiPost(ApiUrl.CONFIRM_PASSWORD, data, { noAuth: true })
);

export const useResetPassword = apiRequestHookFactory(
  apiRedux.resetPassword,
  (data: ResetPasswordReq) =>
    apiPost(ApiUrl.RESET_PASSWORD, data, {
      noAuth: true,
    })
);
